import { useState, useRef, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { Send, Plus, Trash2, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { API, LOGIN_AS_KEY, useApi, useFetch, errMsg } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Markdown } from "../components/Markdown";

const STARTERS = ["Which contractors have payment holds right now?", "Whose PF or ESIC challans are overdue?", "Which workers are missing a UAN or ESIC number?", "Summarise compliance for last month"];

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-[#00E599]/15 text-white" : "c-surface"}`}>
      {m.role === "user" ? <p className="text-sm whitespace-pre-wrap">{m.text}</p> : m.text ? <Markdown text={m.text} /> : <span className="text-xs text-slate-500 animate-pulse">Thinking…</span>}
    </div>
  </div>);

const Sessions = ({ list, active, onPick, onNew, onDelete }) => (
  <div className="c-surface p-3 grid gap-1 content-start max-h-[70vh] overflow-y-auto" data-testid="chat-sessions">
    <button onClick={onNew} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#00E599] hover:bg-[#1A2234]" data-testid="chat-new-btn"><Plus size={14} />New conversation</button>
    {(list || []).map((s) => (
      <div key={s.id} className={`group flex min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer ${active === s.id ? "bg-[#1A2234] text-white" : "text-slate-400 hover:bg-[#1A2234]"}`}
        onClick={() => onPick(s.id)} data-testid="chat-session-item">
        <MessageSquare size={13} className="shrink-0" /><span className="truncate flex-1 min-w-0">{s.title}</span>
        <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }} className="shrink-0 lg:opacity-0 lg:group-hover:opacity-100 text-slate-500 hover:text-red-400" data-testid="chat-session-delete"><Trash2 size={13} /></button>
      </div>))}
  </div>);

export default function Assistant() {
  const api = useApi();
  const { getToken } = useAuth();
  const { data: sessions, reload } = useFetch("/assistant/sessions");
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const asked = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (q && !asked.current) { asked.current = true; window.history.replaceState(null, "", "/assistant"); send(q); }
  });

  const pick = async (id) => {
    try { setMsgs((await api.get(`/assistant/sessions/${id}`)).data); setSid(id); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/assistant/sessions/${id}`); if (id === sid) { setSid(null); setMsgs([]); } reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t }; return c; });
    try {
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` };
      const as = localStorage.getItem(LOGIN_AS_KEY);
      if (as) headers["X-Login-As"] = as;
      const res = await fetch(`${API}/assistant/chat`, { method: "POST", headers, body: JSON.stringify({ message: q, session_id: sid }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "The assistant is unavailable");
      const reader = res.body.getReader(), dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "session") setSid(ev.id);
          else if (ev.type === "delta") append(ev.text);
          else if (ev.type === "error") append(ev.text);
        }
      }
      reload();
    } catch (e) { append(e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="assistant-page">
      <PageHeader eyebrow="Claude AI" title="Compliance assistant" desc="Ask about your workers, wages, challans, scores and payment holds. Answers come from the data you can see in comply369." />
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <Sessions list={sessions} active={sid} onPick={pick} onNew={() => { setSid(null); setMsgs([]); }} onDelete={remove} />
        <div className="c-surface p-4 flex flex-col h-[70vh] min-w-0">
          <div className="flex-1 overflow-y-auto grid gap-3 content-start pr-1" data-testid="chat-messages">
            {!msgs.length && <div className="grid gap-3 py-6" data-testid="chat-empty">
              <div className="flex items-center gap-2 text-slate-300"><Sparkles size={16} className="text-[#00E599]" />Try asking</div>
              <div className="flex flex-wrap gap-2">{STARTERS.map((s, i) => <button key={s} onClick={() => send(s)}
                className="rounded-full border border-[#1F2A3E] px-3 py-1.5 text-xs text-slate-300 hover:border-[#00E599]/40 hover:text-white transition-colors" data-testid={`chat-starter-${i}`}>{s}</button>)}</div>
            </div>}
            {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 pt-3 border-t border-[#1F2A3E]">
            <input className="c-input flex-1" placeholder="Ask about holds, challans, workers, wages…" value={input} maxLength={2000} onChange={(e) => setInput(e.target.value)} disabled={busy} data-testid="chat-input" />
            <button type="submit" disabled={busy || !input.trim()} className="rounded-lg bg-[#00E599] text-[#0B1017] px-4 disabled:opacity-40 hover:bg-[#00E599]/90 transition-colors" data-testid="chat-send-btn"><Send size={15} /></button>
          </form>
          <p className="text-[11px] text-slate-500 mt-2">Answers are written by AI. Check important figures before acting.</p>
        </div>
      </div>
    </div>);
}
